import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Mail, Lock, User, AlertCircle, Check, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuthContext } from "@/lib/AuthProvider";
import { useToast } from "@/hooks/use-toast";
import { NewsletterService } from "@/services/newsletterService";
import AppleIcon from "./icons/AppleIcon";
import GoogleIcon from "./icons/GoogleIcon";
import ForgotPasswordModal from "./ForgotPasswordModal";

interface AuthModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: "login" | "register";
}

const AuthModal = ({ isOpen, onOpenChange, defaultTab = "login" }: AuthModalProps) => {
  const [authTab, setAuthTab] = useState<"login" | "register">(defaultTab);
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [newsletterOptIn, setNewsletterOptIn] = useState(true);
  const [passwordError, setPasswordError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [shouldShowApple, setShouldShowApple] = useState(false);
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  
  // Flag to prevent modal closing during Google user not found handling
  const [isHandlingUserNotFound, setIsHandlingUserNotFound] = useState(false);
  
  // Cache for sign-in methods to avoid repeated API calls
  const [signInMethodsCache] = useState(new Map<string, any>());
  
  const {
    user,
    signInWithGoogle,
    signInWithApple,
    signInWithEmail,
    register,
    checkSignInMethods,
  } = useAuthContext();

  const navigate = useNavigate();
  const { toast } = useToast();

  // Password validation function
  const validatePassword = (password: string) => {
    const requirements = {
      minLength: password.length >= 6,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };
    
    return requirements;
  };

  const passwordRequirements = validatePassword(password);
  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);

  // Cached version of checkSignInMethods to avoid repeated API calls
  const checkSignInMethodsCached = async (email: string) => {
    if (signInMethodsCache.has(email)) {
      return signInMethodsCache.get(email);
    }
    
    const result = await checkSignInMethods(email);
    if (result) {
      signInMethodsCache.set(email, result);
    }
    
    return result;
  };

  // Reset form state when modal opens
  useEffect(() => {
    if (isOpen) {
      setAuthTab(defaultTab);
      setEmail("");
      setConfirmEmail("");
      setPassword("");
      setConfirmPassword("");
      setFirstName("");
      setLastName("");
      setNewsletterOptIn(true);
      setPasswordError("");
      setEmailError("");
      setAuthError("");
      setIsLoading(false);
      setIsHandlingUserNotFound(false);
      // Clear cache when modal opens to ensure fresh data
      signInMethodsCache.clear();
    }
  }, [isOpen, defaultTab, signInMethodsCache]);

  useEffect(() => {
    try {
      const ua = navigator.userAgent || '';
      const isApple = /iPhone|iPad|iPod|Macintosh|Mac OS X/i.test(ua);
      setShouldShowApple(isApple);
    } catch {
      setShouldShowApple(false);
    }
  }, []);

  // Close the modal when user logs in successfully
  useEffect(() => {
    if (user && isOpen && !isHandlingUserNotFound) {
      // Small delay to ensure auth state is fully updated
      const timer = setTimeout(() => {
        onOpenChange(false);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [user, isOpen, onOpenChange, isHandlingUserNotFound]);

  // Validate password confirmation
  useEffect(() => {
    if (confirmPassword && password !== confirmPassword) {
      setPasswordError("Passwords do not match");
    } else {
      setPasswordError("");
    }
  }, [password, confirmPassword]);

  // Validate email confirmation
  useEffect(() => {
    if (confirmEmail && email !== confirmEmail) {
      setEmailError("Email addresses do not match");
    } else {
      setEmailError("");
    }
  }, [email, confirmEmail]);

  const handleEmailLogin = async () => {
    if (!email || !password) {
      setAuthError("Please fill in all fields");
      return;
    }

    setIsLoading(true);
    setAuthError("");

    try {
      // Optional: Check if this email is Google-only before attempting password login
      try {
        const authMethods = await checkSignInMethodsCached(email);
        if (authMethods?.hasGoogleAuth && !authMethods?.hasPasswordAuth) {
          setAuthError("This email is associated with a Google account. Please use 'Sign in with Google' below.");
          toast({
            title: "Google Account Detected",
            description: "This email uses Google sign-in. Please use the Google button below.",
            variant: "destructive",
          });
          return;
        }
      } catch (checkError) {
        // If sign-in method check fails, continue with normal login attempt
      }

      await signInWithEmail(email, password);
      
      toast({
        title: "Welcome back!",
        description: "You have successfully signed in.",
      });
      
      // Explicitly close the modal after successful login
      onOpenChange(false);
    } catch (err: any) {
      console.error("Login error:", err);
      
      // Handle specific Firebase error codes
      switch (err.code) {
        case 'auth/user-not-found':
          setAuthError("No account found with this email address");
          break;
        case 'auth/wrong-password':
          setAuthError("Incorrect password. Please try again");
          break;
        case 'auth/invalid-email':
          setAuthError("Please enter a valid email address");
          break;
        case 'auth/user-disabled':
          setAuthError("This account has been disabled");
          break;
        case 'auth/too-many-requests':
          setAuthError("Too many failed attempts. Please try again later");
          break;
        case 'auth/network-request-failed':
          setAuthError("Network error. Please check your connection");
          break;
        default:
          setAuthError("Failed to sign in. Please try again");
      }
      
      toast({
        title: "Sign In Failed",
        description: "Please check your credentials and try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailRegister = async () => {
    // Validate required fields
    if (!email || !confirmEmail || !password || !confirmPassword || !firstName || !lastName) {
      setAuthError("Please fill in all fields");
      return;
    }

    // Validate emails match before attempting registration
    if (email !== confirmEmail) {
      setEmailError("Email addresses do not match");
      return;
    }

    // Validate passwords match before attempting registration
    if (password !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }

    if (!isPasswordValid) {
      setPasswordError("Password does not meet all requirements");
      return;
    }

    setIsLoading(true);
    setAuthError("");
    setPasswordError("");
    setEmailError("");

    try {
      const fullName = `${firstName} ${lastName}`.trim();
      await register(email, password, fullName, newsletterOptIn);
      
      toast({
        title: "Account Created!",
        description: newsletterOptIn 
          ? "Welcome to JamPack'd! You've been subscribed to our newsletter and can start shopping immediately."
          : "Welcome to JamPack'd! Your account is ready and you can start shopping immediately.",
      });
      
      // Close the modal after successful registration
      onOpenChange(false);
    } catch (err: any) {
      console.error("Registration error:", err);
      
      // Handle specific Firebase error codes
      switch (err.code) {
        case 'auth/email-already-in-use':
          setAuthError("An account with this email already exists");
          break;
        case 'auth/invalid-email':
          setAuthError("Please enter a valid email address");
          break;
        case 'auth/weak-password':
          setPasswordError("Password is too weak. Please choose a stronger password");
          break;
        case 'auth/network-request-failed':
          setAuthError("Network error. Please check your connection");
          break;
        case 'auth/too-many-requests':
          setAuthError("Too many requests. Please try again later");
          break;
        default:
          setAuthError("Failed to create account. Please try again");
      }
      
      toast({
        title: "Registration Failed",
        description: "Please check your information and try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setAuthError("");

    // Set flag before Google sign-in attempt if we're on login tab
    // This prevents modal from closing if user doesn't exist
    if (authTab === 'login') {
      setIsHandlingUserNotFound(true);
    }

    try {
      // Determine if we should create profile based on which tab we're on
      const shouldCreateProfile = authTab === 'register';
      
      const googleUser = await signInWithGoogle(shouldCreateProfile, newsletterOptIn);
      
      // If we get here successfully, reset the flag
      setIsHandlingUserNotFound(false);
      
      const isNewUser = authTab === 'register';
      
      toast({
        title: "Welcome!",
        description: isNewUser && newsletterOptIn
          ? "Account created successfully with Google and you'll be subscribed to our newsletter!"
          : isNewUser
          ? "Account created successfully with Google!"
          : "You have successfully signed in with Google.",
      });
      
      // Explicitly close the modal after successful Google login
      onOpenChange(false);
    } catch (err: any) {
      console.error("Google login error:", err);
      
      // Handle the case where user doesn't exist but tried to sign in
      if (err.message === 'USER_NOT_FOUND') {
        setAuthError("");
        setAuthTab('register');
        
        // Reset the flag after a short delay to allow tab switch
        setTimeout(() => {
          setIsHandlingUserNotFound(false);
        }, 500);
        
        toast({
          title: "Account Not Found",
          description: "No account found with this Google email. Please sign up to create an account.",
          variant: "destructive",
        });
        return;
      }
      
      // Reset flag for other errors
      setIsHandlingUserNotFound(false);
      
      // Handle specific Firebase error codes
      switch (err.code) {
        case 'auth/popup-closed-by-user':
          setAuthError("Sign-in was cancelled");
          break;
        case 'auth/popup-blocked':
          setAuthError("Popup was blocked. Please allow popups and try again");
          break;
        case 'auth/network-request-failed':
          setAuthError("Network error. Please check your connection");
          break;
        case 'auth/too-many-requests':
          setAuthError("Too many requests. Please try again later");
          break;
        case 'auth/account-exists-with-different-credential':
          setAuthError("An account already exists with this email using a different sign-in method");
          break;
        default:
          setAuthError("Failed to sign in with Google. Please try again");
      }
      
      // Only show error toast if it's not a user cancellation or user not found
      if (err.code !== 'auth/popup-closed-by-user' && err.message !== 'USER_NOT_FOUND') {
        toast({
          title: "Google Sign In Failed",
          description: "Please try again or use email/password.",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    setIsLoading(true);
    setAuthError("");

    try {
      const shouldCreateProfile = authTab === 'register';
      const appleUser = await signInWithApple(shouldCreateProfile, newsletterOptIn);

      const isNewUser = authTab === 'register';

      toast({
        title: "Welcome!",
        description: isNewUser && newsletterOptIn
          ? "Account created successfully with Apple and you'll be subscribed to our newsletter!"
          : isNewUser
          ? "Account created successfully with Apple!"
          : "You have successfully signed in with Apple.",
      });

      onOpenChange(false);
      return appleUser;
    } catch (err: any) {
      console.error("Apple login error:", err);

      if (err.message === 'USER_NOT_FOUND') {
        setAuthError("");
        setAuthTab('register');
        toast({
          title: "Account Not Found",
          description: "No account found with this Apple login. Please sign up to create an account.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Apple Sign In Failed",
        description: "Please try again or use another method to continue.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle forgot password functionality
  const handleForgotPassword = async () => {
    if (email) {
      // Check if email is Google-only before opening modal
      try {
        const authMethods = await checkSignInMethodsCached(email);
        if (authMethods?.hasGoogleAuth && !authMethods?.hasPasswordAuth) {
          setAuthError("This email is associated with a Google account. Please use 'Sign in with Google' below.");
          toast({
            title: "Google Account Detected",
            description: "This email uses Google sign-in. Please use the Google button below.",
            variant: "destructive",
          });
          return;
        }
      } catch (error) {
        // If check fails, proceed with forgot password modal anyway
        console.error("Error checking sign-in methods:", error);
      }
    }
    
    // Open forgot password modal
    onOpenChange(false);
    setForgotPasswordOpen(true);
  };

  const handleBackToLogin = () => {
    setForgotPasswordOpen(false);
    onOpenChange(true);
    setAuthTab("login");
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md w-full max-h-[90vh] p-0 gap-0 bg-white rounded-lg shadow-xl border-0 overflow-hidden flex flex-col">
          <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground z-10">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogClose>

          <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4 bg-gradient-to-r from-jamaica-green to-jamaica-green/90">
            <DialogTitle className="text-2xl font-bold text-white text-center">
              {authTab === "login" ? "Welcome Back" : "Create Account"}
            </DialogTitle>
            <DialogDescription className="text-white/90 text-center">
              {authTab === "login"
                ? "Sign in to your account to continue"
                : "Join us and start your journey today"}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto min-h-0">
            <Tabs
              defaultValue={authTab}
              value={authTab}
              onValueChange={(value) => setAuthTab(value as "login" | "register")}
              className="p-6 pb-4"
            >
              <TabsList className="grid grid-cols-2 mb-6">
                <TabsTrigger value="login">Log In</TabsTrigger>
                <TabsTrigger value="register">Sign Up</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Button
                      variant="outline"
                      className="py-5 flex gap-2 bg-white border-gray-300 hover:bg-gray-50 text-gray-700 font-medium"
                      onClick={handleGoogleLogin}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <GoogleIcon size={18} />
                      )}
                      <span>
                        {isLoading ? "Signing In..." : "Sign in with Google"}
                      </span>
                    </Button>
                    {shouldShowApple && (
                      <Button
                        variant="outline"
                        className="py-5 flex gap-2 bg-black text-white hover:bg-black/80 hover:text-white font-medium border-transparent"
                        onClick={handleAppleLogin}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <AppleIcon className="h-4 w-4" />
                        )}
                        <span>
                          {isLoading ? "Signing In..." : "Sign in with Apple"}
                        </span>
                      </Button>
                    )}
                  </div>

                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="bg-white px-2 text-gray-500">
                        or continue with email
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="loginEmail"
                      className="block text-base font-medium text-gray-700"
                    >
                      Email Address
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input
                        type="email"
                        id="loginEmail"
                        placeholder="your@email.com"
                        className="pl-10 py-2"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="loginPassword"
                      className="block text-base font-medium text-gray-700"
                    >
                      Password
                    </Label>

                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input
                        type="password"
                        id="loginPassword"
                        placeholder="••••••••"
                        className="pl-10 py-2"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        className="text-sm text-jamaica-green font-medium hover:underline focus:outline-none"
                        onClick={handleForgotPassword}
                      >
                        Forgot password?
                      </button>
                    </div>
                  </div>

                  {authError && (
                    <div className="flex items-center text-red-600 text-sm bg-red-50 p-3 rounded-md">
                      <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                      {authError}
                    </div>
                  )}

                  <Button
                    className="w-full bg-jamaica-green hover:bg-jamaica-green/90 text-white py-6"
                    onClick={handleEmailLogin}
                    disabled={isLoading || !email || !password}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing In...
                      </>
                    ) : (
                      "Log In"
                    )}
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="register">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Button
                      variant="outline"
                      className="py-5 flex gap-2 bg-white border-gray-300 hover:bg-gray-50 text-gray-700 font-medium"
                      onClick={handleGoogleLogin}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <GoogleIcon size={18} />
                      )}
                      <span>
                        {isLoading ? "Signing up..." : "Sign up with Google"}
                      </span>
                    </Button>
                    {shouldShowApple && (
                      <Button
                        variant="outline"
                        className="py-5 flex gap-2 bg-black text-white hover:bg-black/80 hover:text-white font-medium border-transparent"
                        onClick={handleAppleLogin}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <AppleIcon className="h-4 w-4" />
                        )}
                        <span>
                          {isLoading ? "Signing up..." : "Sign up with Apple"}
                        </span>
                      </Button>
                    )}
                  </div>

                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="bg-white px-2 text-gray-500">
                        or continue with email
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label
                        htmlFor="registerFirstName"
                        className="block text-base font-medium text-gray-700"
                      >
                        First Name
                      </Label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                        <Input
                          type="text"
                          id="registerFirstName"
                          placeholder="First Name"
                          className="pl-10 py-2"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="registerLastName"
                        className="block text-base font-medium text-gray-700"
                      >
                        Last Name
                      </Label>
                      <Input
                        type="text"
                        id="registerLastName"
                        placeholder="Last Name"
                        className="py-2"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="registerEmail"
                      className="block text-base font-medium text-gray-700"
                    >
                      Email Address
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input
                        type="email"
                        id="registerEmail"
                        placeholder="your@email.com"
                        className="pl-10 py-2"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="registerConfirmEmail"
                      className="block text-base font-medium text-gray-700"
                    >
                      Confirm Email Address
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input
                        type="email"
                        id="registerConfirmEmail"
                        placeholder="your@email.com"
                        className={`pl-10 py-2 ${
                          confirmEmail && email !== confirmEmail
                            ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                            : confirmEmail && email === confirmEmail
                            ? "border-green-300 focus:border-green-500 focus:ring-green-500"
                            : ""
                        }`}
                        value={confirmEmail}
                        onChange={(e) => setConfirmEmail(e.target.value)}
                        onPaste={(e) => e.preventDefault()}
                      />
                      {confirmEmail && (
                        <div className="absolute right-3 top-3">
                          {email === confirmEmail ? (
                            <div className="h-5 w-5 text-green-500">
                              <svg fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          ) : (
                            <AlertCircle className="h-5 w-5 text-red-500" />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  {emailError && (
                    <div className="flex items-center text-red-500 text-sm mt-1">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {emailError}
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label
                      htmlFor="registerPassword"
                      className="block text-base font-medium text-gray-700"
                    >
                      Password
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input
                        type="password"
                        id="registerPassword"
                        placeholder="••••••••"
                        className="pl-10 py-2"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>
                    {password && (
                      <div className="mt-2 p-3 bg-gray-50 rounded-md border">
                        <p className="text-sm font-medium text-gray-700 mb-2">Password Requirements:</p>
                        <div className="space-y-1">
                          <div className={`flex items-center text-xs ${passwordRequirements.minLength ? 'text-green-600' : 'text-gray-500'}`}>
                            {passwordRequirements.minLength ? (
                              <Check className="h-3 w-3 mr-2" />
                            ) : (
                              <div className="h-3 w-3 mr-2 rounded-full border border-gray-300"></div>
                            )}
                            At least 6 characters
                          </div>
                          <div className={`flex items-center text-xs ${passwordRequirements.hasUppercase ? 'text-green-600' : 'text-gray-500'}`}>
                            {passwordRequirements.hasUppercase ? (
                              <Check className="h-3 w-3 mr-2" />
                            ) : (
                              <div className="h-3 w-3 mr-2 rounded-full border border-gray-300"></div>
                            )}
                            One uppercase letter (A-Z)
                          </div>
                          <div className={`flex items-center text-xs ${passwordRequirements.hasLowercase ? 'text-green-600' : 'text-gray-500'}`}>
                            {passwordRequirements.hasLowercase ? (
                              <Check className="h-3 w-3 mr-2" />
                            ) : (
                              <div className="h-3 w-3 mr-2 rounded-full border border-gray-300"></div>
                            )}
                            One lowercase letter (a-z)
                          </div>
                          <div className={`flex items-center text-xs ${passwordRequirements.hasNumber ? 'text-green-600' : 'text-gray-500'}`}>
                            {passwordRequirements.hasNumber ? (
                              <Check className="h-3 w-3 mr-2" />
                            ) : (
                              <div className="h-3 w-3 mr-2 rounded-full border border-gray-300"></div>
                            )}
                            One number (0-9)
                          </div>
                          <div className={`flex items-center text-xs ${passwordRequirements.hasSpecialChar ? 'text-green-600' : 'text-gray-500'}`}>
                            {passwordRequirements.hasSpecialChar ? (
                              <Check className="h-3 w-3 mr-2" />
                            ) : (
                              <div className="h-3 w-3 mr-2 rounded-full border border-gray-300"></div>
                            )}
                            One special character (!@#$%^&*)
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="registerConfirmPassword"
                      className="block text-base font-medium text-gray-700"
                    >
                      Confirm Password
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input
                        type="password"
                        id="registerConfirmPassword"
                        placeholder="••••••••"
                        className={`pl-10 py-2 ${
                          confirmPassword && password !== confirmPassword
                            ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                            : confirmPassword && password === confirmPassword
                            ? "border-green-300 focus:border-green-500 focus:ring-green-500"
                            : ""
                        }`}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                      {confirmPassword && (
                        <div className="absolute right-3 top-3">
                          {password === confirmPassword ? (
                            <div className="h-5 w-5 text-green-500">
                              <svg fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          ) : (
                            <AlertCircle className="h-5 w-5 text-red-500" />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  {passwordError && (
                    <div className="flex items-center text-red-500 text-sm mt-1">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {passwordError}
                    </div>
                  )}

                  {authError && (
                    <div className="flex items-center text-red-600 text-sm bg-red-50 p-3 rounded-md">
                      <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                      {authError}
                    </div>
                  )}

                  {/* Newsletter Opt-in */}
                  <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <Checkbox
                      id="newsletter-optin"
                      checked={newsletterOptIn}
                      onCheckedChange={(checked) => setNewsletterOptIn(checked as boolean)}
                      className="mt-0.5"
                    />
                    <div className="space-y-1">
                      <Label 
                        htmlFor="newsletter-optin" 
                        className="text-sm font-medium text-gray-900 cursor-pointer leading-none"
                      >
                        Subscribe to our newsletter
                      </Label>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        Get exclusive offers, new product notifications, and a dash of Jamaican sunshine in your inbox.
                      </p>
                    </div>
                  </div>

                  <Button
                    className="w-full bg-jamaica-green hover:bg-jamaica-green/90 text-white py-6 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleEmailRegister}
                    disabled={
                      isLoading ||
                      !email || 
                      !confirmEmail ||
                      !password || 
                      !confirmPassword || 
                      !firstName || 
                      !lastName || 
                      email !== confirmEmail ||
                      password !== confirmPassword ||
                      !isPasswordValid
                    }
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating Account...
                      </>
                    ) : (
                      "Create Account"
                    )}
                  </Button>

                  {/* Terms & Privacy consent note */}
                  <p className="text-[11px] text-gray-500 text-center leading-snug">
                    By creating an account, you agree to our
                    {' '}<a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-jamaica-green hover:underline font-medium"
                    >
                      Terms of Service
                    </a>
                    {' '}and{' '}
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-jamaica-green hover:underline font-medium"
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        isOpen={forgotPasswordOpen}
        onOpenChange={setForgotPasswordOpen}
        onBackToLogin={handleBackToLogin}
      />
    </>
  );
};

export default AuthModal; 
